import { FC } from 'react';

import { AppDashboard } from '@shared/types/app';
import Markdown from 'markdown-to-jsx';

import { useAnalytics } from '@hooks/useAnalytics';
import { useDynamicAction } from '@hooks/useDynamicAction';

import { useRouter } from '@router/index';

import { FontAwesomeIcon } from '@components/icons/FontAwesomeIcon';
import { Skeleton } from '@components/layout/Skeleton/Skeleton';
import { TitleToolbar } from '@components/layout/TitleToolbar/TitleToolbar';

import { SharedDashboardSectionProps } from '../DashboardScreen.types';

export const FeaturedSection: FC<{
  section: NonNullable<AppDashboard['featuredFooter']>['sections'][number];
  back?: string;
}> = ({ section, back = '/' }) => {
  const { track } = useAnalytics();
  const { handleDynamicAction } = useDynamicAction();

  const featuredIconSize = 24;

  return (
    <div
      className="d-flex align-items-center gap-2 p-2"
      style={{ background: 'rgba(255,255,255,.1)', borderRadius: 8 }}
      onClick={() => {
        track('Featured Row Clicked', section);
        handleDynamicAction(section, {
          state: { back, forceBack: true },
        });
      }}
    >
      <div
        style={{
          height: featuredIconSize,
          width: featuredIconSize,
        }}
      >
        <img
          className="d-block"
          src={section.imgUrl}
          alt={section.title}
          style={{
            width: '100%',
            height: 'auto',
            maxHeight: '100%',
            objectFit: 'contain',
            borderRadius: 2,
          }}
        />
      </div>
      <div className="d-flex gap-2 align-items-center">
        <strong>{section.title}</strong>
        {section.titleAfter && (
          <Markdown options={{ forceWrapper: true }}>{section.titleAfter}</Markdown>
        )}
      </div>
      <div className="ml-auto px-2 d-flex align-items-center gap-3">
        <div>{section.count ?? 0}</div>
        <FontAwesomeIcon name="chevron-right" />
      </div>
    </div>
  );
};

export const Featured: FC<SharedDashboardSectionProps> = ({
  isAppDashboardReady,
  appDashboard,
}) => {
  if (!isAppDashboardReady) {
    return (
      <>
        <div>
          <div className="mb-2">
            <Skeleton height={22} />
          </div>
          <div className="mb-3">
            <Skeleton height={48} />
            <Skeleton height={48} />
            <Skeleton height={48} />
            <Skeleton height={48} />
          </div>
        </div>
      </>
    );
  }

  if (!appDashboard?.featuredFooter) {
    return <></>;
  }

  const { title, sections } = appDashboard?.featuredFooter;

  return (
    <>
      <TitleToolbar text={title} size="sm" className="mb-1" />
      <div className="mt-2">
        <div className="d-grid gap-2 mb-3">
          {sections.map((section) => (
            <FeaturedSection key={section.title} section={section} />
          ))}
        </div>
      </div>
    </>
  );
};
